/**
 * Floating horizontal head modal — swipe-in install nudge after a failed
 * native open attempt. Twin elevation to FloatingFooter; pinned to top.
 *
 * Icon: store-facing native mark (`adaptive-icon-native.png`) styled as a
 * phone home-screen icon (rounded square + soft shadow).
 */
import { useEffect, useState } from 'react';
import { MARKETING_SITE_URL } from '../config/webOrigins';

/** Copied from apps/mobile/assets/adaptive-icon-native.png → public/icons. */
const NATIVE_APP_ICON_SRC = '/icons/app-icon-native.png';

interface Props {
  open: boolean;
  onDismiss: () => void;
  className?: string;
}

export default function AppInstallHeadBanner({ open, onDismiss, className = '' }: Props) {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    if (!open) {
      setEntered(false);
      return undefined;
    }
    const id = window.requestAnimationFrame(() => setEntered(true));
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className={`pointer-events-none fixed inset-x-2 z-[60] ${className}`}
      style={{ top: 'max(0.5rem, env(safe-area-inset-top))' }}
      role="dialog"
      aria-label="Get the X9 app"
    >
      <div
        className={[
          'pointer-events-auto flex items-center gap-3 rounded-[15px] bg-white px-3 py-2.5',
          'shadow-[0_8px_28px_rgba(0,0,0,0.28)]',
          'transition duration-300 ease-out motion-reduce:transition-none',
          entered ? 'translate-y-0 opacity-100' : '-translate-y-3 opacity-0',
        ].join(' ')}
      >
        <img
          src={NATIVE_APP_ICON_SRC}
          alt=""
          width={44}
          height={44}
          draggable={false}
          className="size-11 shrink-0 select-none rounded-[11px] object-cover shadow-[0_2px_8px_rgba(42,60,82,0.28)] ring-1 ring-black/5"
          aria-hidden="true"
        />

        <div className="min-w-0 flex-1 text-left">
          <p className="text-sm font-semibold leading-tight text-x9-ink">
            Get the X9 app
          </p>
          <p className="mt-0.5 text-xs leading-snug text-x9-marineSoft">
            Faster capture, follow, and task updates.
          </p>
        </div>

        <a
          href={MARKETING_SITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-paper-primary shrink-0 !px-3 !py-2 !text-xs"
        >
          Get app
        </a>

        <button
          type="button"
          onClick={onDismiss}
          className="flex size-8 shrink-0 items-center justify-center rounded-lg text-x9-marineSoft transition hover:bg-x9-marine/5 hover:text-x9-ink"
          aria-label="Dismiss"
        >
          <span aria-hidden="true" className="text-lg leading-none">
            ×
          </span>
        </button>
      </div>
    </div>
  );
}
